import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFinance } from '@/hooks/useFinance';
import { useAuth } from '@/hooks/useAuth';
import { useExpenseCategories } from '@/hooks/useExpenseCategories';
import { uploadMediaFile } from '@/hooks/useUpload';
import { buildExcelDocument } from '@/lib/excelExport';
import {
  formatDate,
  formatMoney,
  inRange,
  periodRange,
  type Period,
} from '@/lib/finance';
import { FinanceSection, FinanceEmpty, FinanceModal, Field, inputClass, PeriodFilter } from '../components/FinanceUI';

export default function FinanceDepensesPage() {
  const finance = useFinance();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { currency, expenses, accounts, loading } = finance;
  const { categories, metaFor, addCategory } = useExpenseCategories();

  const [period, setPeriod] = useState<Period>('month');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [recurringOpen, setRecurringOpen] = useState(false);
  const [catModalOpen, setCatModalOpen] = useState(false);
  const [newCatLabel, setNewCatLabel] = useState('');
  const [newCatIcon, setNewCatIcon] = useState('ri-price-tag-3-line');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [creatingExcel, setCreatingExcel] = useState(false);

  const [amount, setAmount] = useState('');
  const [category, setCategory] = useState('');
  const [description, setDescription] = useState('');
  const [vendor, setVendor] = useState('');
  const [accountId, setAccountId] = useState('');
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [receiptFile, setReceiptFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);

  const [recAmount, setRecAmount] = useState('');
  const [recCategory, setRecCategory] = useState('');
  const [recFrequency, setRecFrequency] = useState('monthly');
  const [recNext, setRecNext] = useState('');
  const [recDescription, setRecDescription] = useState('');

  // Catégorie par défaut dès que la liste (chargée depuis la base) est disponible.
  useEffect(() => {
    if (categories.length === 0) return;
    const keys = categories.map((c) => c.key);
    setCategory((prev) => (prev && keys.includes(prev) ? prev : categories[0].key));
    setRecCategory((prev) => (prev && keys.includes(prev) ? prev : categories[0].key));
  }, [categories]);

  const range = useMemo(() => periodRange(period, customStart, customEnd), [period, customStart, customEnd]);
  const inPeriod = useMemo(() => expenses.filter((e) => inRange(e.occurred_at, range)), [expenses, range]);
  const total = inPeriod.reduce((s, e) => s + Number(e.amount), 0);

  const byCategory = useMemo(() => {
    const map: Record<string, number> = {};
    inPeriod.forEach((e) => { map[e.category] = (map[e.category] || 0) + Number(e.amount); });
    return Object.entries(map).sort((a, b) => b[1] - a[1]);
  }, [inPeriod]);

  const reset = () => {
    setAmount(''); setCategory(categories[0]?.key || ''); setDescription(''); setVendor(''); setAccountId(''); setReceiptFile(null);
    setDate(new Date().toISOString().slice(0, 10));
  };

  const submit = async () => {
    const value = parseFloat(amount);
    if (!value || value <= 0) { setMsg('Montant invalide'); return; }
    if (!category) { setMsg('Choisissez une catégorie'); return; }
    setBusy(true);
    try {
      let receiptUrl: string | null = null;
      if (receiptFile) {
        setUploading(true);
        try {
          receiptUrl = await uploadMediaFile(receiptFile, 'finance-receipts');
        } catch {
          receiptUrl = null;
        }
        setUploading(false);
      }
      await finance.addExpense({
        amount: value,
        category,
        description: description || metaFor(category).label,
        vendor: vendor || null,
        account_id: accountId ? Number(accountId) : null,
        occurred_at: new Date(date).toISOString(),
        receipt_url: receiptUrl,
      });
      reset();
      setModalOpen(false);
    } catch (err) {
      setMsg(err instanceof Error ? err.message : 'Erreur');
    } finally {
      setBusy(false);
      setUploading(false);
    }
  };

  const handleOpenInExcel = async () => {
    if (!user || inPeriod.length === 0) return;
    setCreatingExcel(true);
    try {
      const headers = ['Date', 'Catégorie', 'Description', 'Fournisseur / bénéficiaire', 'Montant', 'Devise', 'Justificatif'];
      const rows = inPeriod.map((e) => [
        formatDate(e.occurred_at),
        metaFor(e.category).label,
        e.description || '',
        e.vendor || '',
        String(Number(e.amount)),
        currency,
        e.receipt_url ? 'Oui' : '',
      ]);
      await buildExcelDocument(
        user.id,
        `Dépenses ${new Date().toISOString().slice(0, 10)}`,
        headers,
        rows,
        { sheetName: 'Dépenses' },
      );
      navigate('/dashboard/file-manager');
    } catch {
      /* erreur déjà gérée côté documents */
    } finally {
      setCreatingExcel(false);
    }
  };

  const submitRecurring = async () => {
    const value = parseFloat(recAmount);
    if (!value || value <= 0) { setMsg('Montant récurrent invalide'); return; }
    if (!recCategory) { setMsg('Choisissez une catégorie'); return; }
    setBusy(true);
    try {
      await finance.addRecurring({
        category: recCategory,
        amount: value,
        currency,
        frequency: recFrequency,
        next_due_date: recNext || null,
        account_id: null,
        description: recDescription || metaFor(recCategory).label,
        is_active: true,
      });
      setRecAmount(''); setRecDescription(''); setRecNext('');
      setRecurringOpen(false);
    } catch (err) {
      setMsg(err instanceof Error ? err.message : 'Erreur');
    } finally {
      setBusy(false);
    }
  };

  const submitCategory = async () => {
    if (!newCatLabel.trim()) return;
    setBusy(true);
    setMsg(null);
    try {
      const created = await addCategory(newCatLabel, newCatIcon);
      setCategory(created.key);
      setRecCategory(created.key);
      setNewCatLabel('');
      setNewCatIcon('ri-price-tag-3-line');
      setCatModalOpen(false);
    } catch (err) {
      setMsg(err instanceof Error ? err.message : 'Erreur');
    } finally {
      setBusy(false);
    }
  };

  const categoryField = (value: string, onChange: (v: string) => void) => (
    <div className="mb-3">
      <div className="flex items-center justify-between mb-1">
        <span className="block text-xs font-semibold text-foreground-700">Catégorie</span>
        <button
          type="button"
          onClick={() => { setMsg(null); setCatModalOpen(true); }}
          className="text-xs text-primary-600 hover:text-primary-700 font-medium cursor-pointer whitespace-nowrap"
        >
          <i className="ri-add-line"></i> Nouveau type
        </button>
      </div>
      <select value={value} onChange={(e) => onChange(e.target.value)} className={inputClass}>
        {categories.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
      </select>
    </div>
  );

  if (loading) {
    return <div className="flex items-center justify-center py-20"><i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i></div>;
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
        <PeriodFilter value={period} onChange={setPeriod} customStart={customStart} customEnd={customEnd} onCustomChange={(s, e) => { setCustomStart(s); setCustomEnd(e); }} />
        <div className="flex items-center gap-2">
          <button onClick={handleOpenInExcel} disabled={creatingExcel || inPeriod.length === 0} className="flex items-center gap-1.5 px-3.5 py-2 bg-accent-500 text-background-50 rounded-full text-sm font-medium whitespace-nowrap hover:bg-accent-600 disabled:opacity-50 transition-colors cursor-pointer">
            {creatingExcel ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-file-excel-2-line"></i>}
            Ouvrir dans Excel
          </button>
          <button onClick={() => { reset(); setModalOpen(true); }} className="flex items-center gap-1.5 px-4 py-2 bg-primary-500 text-background-50 rounded-full text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer">
            <i className="ri-add-line"></i>Ajouter une dépense
          </button>
        </div>
      </div>

      {msg && <div className="px-4 py-2.5 rounded-lg bg-secondary-50 text-secondary-800 text-sm">{msg}</div>}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="bg-accent-50 border border-accent-200/60 rounded-lg p-4 sm:col-span-1">
          <span className="text-xs text-accent-800">Dépenses de la période</span>
          <div className="text-xl font-bold font-heading text-accent-700 mt-1">{formatMoney(total, currency)}</div>
        </div>
        <div className="sm:col-span-2 bg-background-50 border border-background-200/70 rounded-lg p-4">
          <span className="text-xs text-foreground-500">Répartition par catégorie</span>
          {byCategory.length === 0 ? (
            <p className="text-xs text-foreground-400 mt-1">Aucune dépense sur la période.</p>
          ) : (
            <div className="flex flex-wrap gap-1.5 mt-2">
              {byCategory.slice(0, 8).map(([cat, val]) => {
                const meta = metaFor(cat);
                return (
                  <span key={cat} className="flex items-center gap-1 px-2 py-1 rounded-full bg-background-100 text-xs text-foreground-700">
                    <i className={`${meta.icon} text-accent-600`}></i>{meta.label}
                    <strong className="font-semibold">{formatMoney(val, currency)}</strong>
                  </span>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <FinanceSection
        title="Dépenses récurrentes"
        icon="ri-loop-left-line"
        action={
          <button onClick={() => setRecurringOpen(true)} className="text-xs text-primary-600 hover:text-primary-700 font-medium cursor-pointer whitespace-nowrap">
            <i className="ri-add-line"></i> Ajouter
          </button>
        }
      >
        {finance.recurring.length === 0 ? (
          <p className="text-sm text-foreground-400">Aucune dépense récurrente (loyer, internet, salaires...).</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {finance.recurring.map((r) => {
              const meta = metaFor(r.category);
              return (
                <div key={r.id} className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${r.is_active ? 'bg-background-50 border-background-200/70' : 'bg-background-100 border-transparent opacity-60'}`}>
                  <i className={`${meta.icon} text-accent-600`}></i>
                  <div className="text-xs">
                    <div className="font-medium text-foreground-800">{meta.label} — {formatMoney(Number(r.amount), currency)}</div>
                    <div className="text-foreground-400">{r.frequency === 'monthly' ? 'Mensuel' : r.frequency === 'weekly' ? 'Hebdo' : 'Annuel'}{r.next_due_date ? ` · ${formatDate(r.next_due_date)}` : ''}</div>
                  </div>
                  <button onClick={() => finance.toggleRecurring(r.id, !r.is_active)} className="w-6 h-6 flex items-center justify-center rounded text-foreground-400 hover:text-primary-600 cursor-pointer" title={r.is_active ? 'Désactiver' : 'Activer'}>
                    <i className={`${r.is_active ? 'ri-toggle-fill text-primary-600' : 'ri-toggle-line'} text-lg`}></i>
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </FinanceSection>

      <FinanceSection title={`Dépenses (${inPeriod.length})`} icon="ri-shopping-bag-3-line">
        {inPeriod.length === 0 ? (
          <FinanceEmpty icon="ri-shopping-bag-3-line" title="Aucune dépense sur cette période" hint="Enregistrez vos achats, loyer, transport, salaires..." />
        ) : (
          <div className="divide-y divide-background-200/70">
            {inPeriod.map((e) => {
              const meta = metaFor(e.category);
              return (
                <div key={e.id} className="flex items-center gap-3 py-3">
                  <span className="w-9 h-9 rounded-lg bg-accent-100 text-accent-700 flex items-center justify-center flex-shrink-0">
                    <i className={`${meta.icon} text-sm`}></i>
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-foreground-900 truncate">{e.description || meta.label}</div>
                    <div className="text-xs text-foreground-400 flex items-center gap-2 flex-wrap">
                      <span>{formatDate(e.occurred_at)}</span>
                      <span className="px-1.5 py-0.5 rounded-full bg-background-100 text-foreground-600">{meta.label}</span>
                      {e.vendor && <span><i className="ri-store-2-line"></i> {e.vendor}</span>}
                      {e.receipt_url && <a href={e.receipt_url} target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline"><i className="ri-attachment-line"></i> Justif.</a>}
                    </div>
                  </div>
                  <span className="text-sm font-bold text-foreground-800 whitespace-nowrap">− {formatMoney(Number(e.amount), currency)}</span>
                  <button onClick={() => finance.deleteExpense(e.id)} className="w-7 h-7 flex items-center justify-center rounded-md text-foreground-400 hover:text-red-500 hover:bg-red-50 transition-colors cursor-pointer" title="Supprimer">
                    <i className="ri-delete-bin-line text-sm"></i>
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </FinanceSection>

      {modalOpen && (
        <FinanceModal title="Ajouter une dépense" onClose={() => setModalOpen(false)}>
          <Field label="Montant *">
            <input type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} className={inputClass} placeholder="0.00" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            {categoryField(category, setCategory)}
            <Field label="Date">
              <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className={inputClass} />
            </Field>
          </div>
          <Field label="Fournisseur / bénéficiaire">
            <input value={vendor} onChange={(e) => setVendor(e.target.value)} className={inputClass} placeholder="Ex : Marjane, Taxi..." />
          </Field>
          <Field label="Description">
            <input value={description} onChange={(e) => setDescription(e.target.value)} className={inputClass} placeholder="Ex : Achat marchandise" />
          </Field>
          {accounts.length > 0 && (
            <Field label="Payer depuis le compte">
              <select value={accountId} onChange={(e) => setAccountId(e.target.value)} className={inputClass}>
                <option value="">— Aucun compte —</option>
                {accounts.map((a) => <option key={a.id} value={String(a.id)}>{a.name}</option>)}
              </select>
            </Field>
          )}
          <Field label="Justificatif (facture / reçu)">
            <label className="flex items-center gap-2 px-3 py-2 rounded-lg border border-dashed border-background-300/60 text-sm text-foreground-500 hover:border-primary-300 hover:text-primary-600 cursor-pointer transition-colors">
              <i className="ri-attachment-2"></i>
              {receiptFile ? receiptFile.name : 'Joindre un fichier'}
              <input type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => setReceiptFile(e.target.files?.[0] || null)} />
            </label>
          </Field>
          <button onClick={submit} disabled={busy} className="w-full mt-2 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 disabled:opacity-50 transition-colors cursor-pointer whitespace-nowrap">
            {busy ? <i className="ri-loader-4-line animate-spin"></i> : uploading ? 'Envoi du justificatif...' : 'Enregistrer la dépense'}
          </button>
        </FinanceModal>
      )}

      {recurringOpen && (
        <FinanceModal title="Nouvelle dépense récurrente" onClose={() => setRecurringOpen(false)}>
          <Field label="Montant *">
            <input type="number" min="0" step="0.01" value={recAmount} onChange={(e) => setRecAmount(e.target.value)} className={inputClass} placeholder="0.00" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            {categoryField(recCategory, setRecCategory)}
            <Field label="Fréquence">
              <select value={recFrequency} onChange={(e) => setRecFrequency(e.target.value)} className={inputClass}>
                <option value="monthly">Mensuel</option>
                <option value="weekly">Hebdomadaire</option>
                <option value="yearly">Annuel</option>
              </select>
            </Field>
          </div>
          <Field label="Prochaine échéance">
            <input type="date" value={recNext} onChange={(e) => setRecNext(e.target.value)} className={inputClass} />
          </Field>
          <Field label="Description">
            <input value={recDescription} onChange={(e) => setRecDescription(e.target.value)} className={inputClass} placeholder="Ex : Loyer boutique" />
          </Field>
          <button onClick={submitRecurring} disabled={busy} className="w-full mt-2 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 disabled:opacity-50 transition-colors cursor-pointer whitespace-nowrap">
            {busy ? <i className="ri-loader-4-line animate-spin"></i> : 'Enregistrer'}
          </button>
        </FinanceModal>
      )}

      {catModalOpen && (
        <>
          <div className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center">
            <div className="absolute inset-0 bg-black/30 animate-fade-in" onClick={() => setCatModalOpen(false)} />
            <div className="relative bg-background-50 rounded-t-2xl sm:rounded-2xl w-full sm:max-w-md mx-0 sm:mx-4 max-h-[90vh] flex flex-col animate-scale-in">
              <div className="flex items-center justify-between px-5 h-14 border-b border-background-200/70 flex-shrink-0">
                <h3 className="text-sm font-bold text-foreground-950">Nouveau type de dépense</h3>
                <button onClick={() => setCatModalOpen(false)} className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-background-100 transition-colors cursor-pointer">
                  <i className="ri-close-line text-foreground-500"></i>
                </button>
              </div>
              <div className="overflow-y-auto p-5">
                <Field label="Nom du type *">
                  <input
                    autoFocus
                    value={newCatLabel}
                    onChange={(e) => setNewCatLabel(e.target.value)}
                    className={inputClass}
                    placeholder="Ex : Emballage, Publicité, Réparations..."
                    maxLength={40}
                  />
                </Field>
                <Field label="Icône">
                  <div className="flex flex-wrap gap-1.5">
                    {[
                      'ri-price-tag-3-line', 'ri-shopping-bag-3-line', 'ri-truck-line', 'ri-tools-line',
                      'ri-user-star-line', 'ri-megaphone-line', 'ri-bank-line', 'ri-file-list-3-line',
                      'ri-flashlight-line', 'ri-wifi-line', 'ri-home-4-line', 'ri-more-line',
                    ].map((ic) => (
                      <button
                        key={ic}
                        type="button"
                        onClick={() => setNewCatIcon(ic)}
                        className={`w-9 h-9 flex items-center justify-center rounded-lg border transition-colors cursor-pointer ${newCatIcon === ic ? 'border-primary-300 bg-primary-50 text-primary-700' : 'border-background-200/70 text-foreground-500 hover:bg-background-100'}`}
                      >
                        <i className={`${ic} text-base`}></i>
                      </button>
                    ))}
                  </div>
                </Field>
                <p className="text-[11px] text-foreground-400 flex items-start gap-1">
                  <i className="ri-information-line mt-0.5"></i>
                  Ce type sera disponible uniquement dans votre compte, en plus des types par défaut.
                </p>
                <button
                  onClick={submitCategory}
                  disabled={busy || !newCatLabel.trim()}
                  className="w-full mt-3 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 disabled:opacity-50 transition-colors cursor-pointer whitespace-nowrap"
                >
                  {busy ? <i className="ri-loader-4-line animate-spin"></i> : 'Créer le type'}
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}